import { z } from 'zod';

/** Versión de esquema de las respuestas y documentos del contrato v1. */
export const SCHEMA_VERSION = 1 as const;
export const CONTRACT_VERSION = '1.0.0' as const;
/** Native execution clients. Antigravity denotes agy CLI; IDE configuration is separate. */
export const AgentClient = z.enum(['codex', 'claude', 'antigravity']);
export type AgentClient = z.infer<typeof AgentClient>;

export const Uuid = z.uuid();
export const OperationIdField = z.uuid().describe('Identificador de operación generado por el cliente (idempotencia).');
export const Timestamp = z.string().describe('Marca temporal del servidor (ISO 8601 con zona).');
export const Cursor = z.string().max(512).nullable().optional();
export const Limit = z.int().min(1).max(200).optional();
export const IanaTimezone = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[A-Za-z_]+(\/[A-Za-z0-9_+-]+)*$|^UTC$/, 'Zona IANA inválida');
export const HttpsUrl = z.url({ protocol: /^https$/ }).max(2000);
export const Sha256 = z.string().regex(/^[0-9a-f]{64}$/, 'SHA-256 en hexadecimal');

export const ERROR_CODES = [
  'AUTH_REQUIRED',
  'FORBIDDEN',
  'NOT_FOUND',
  'REVISION_CONFLICT',
  'CONFLICT',
  'VALIDATION_FAILED',
  'QUOTA_EXCEEDED',
  'DEVICE_REQUIRED',
  'CAPABILITY_UNAVAILABLE',
  'STALE_JOB_ATTEMPT',
  'PROVIDER_ACTION_REQUIRED',
  'IDEMPOTENCY_MISMATCH',
  'DOCUMENT_DELETED',
  'INVALID_TRANSITION',
  'CURSOR_EXPIRED',
  'RATE_LIMITED',
  'INTERNAL',
] as const;
export const ErrorCode = z.enum(ERROR_CODES);
export type ErrorCode = z.infer<typeof ErrorCode>;

/** Códigos en los que reintentar la misma operación puede tener éxito. */
export const RETRYABLE_CODES: ReadonlySet<ErrorCode> = new Set(['RATE_LIMITED', 'INTERNAL']);

export const HTTP_STATUS: Record<ErrorCode, number> = {
  AUTH_REQUIRED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  REVISION_CONFLICT: 409,
  CONFLICT: 409,
  VALIDATION_FAILED: 422,
  QUOTA_EXCEEDED: 413,
  DEVICE_REQUIRED: 409,
  CAPABILITY_UNAVAILABLE: 409,
  STALE_JOB_ATTEMPT: 409,
  PROVIDER_ACTION_REQUIRED: 409,
  IDEMPOTENCY_MISMATCH: 409,
  DOCUMENT_DELETED: 410,
  INVALID_TRANSITION: 409,
  CURSOR_EXPIRED: 410,
  RATE_LIMITED: 429,
  INTERNAL: 500,
};

export const ApiErrorBody = z.object({
  code: ErrorCode,
  message: z.string(),
  retryable: z.boolean(),
  details: z.record(z.string(), z.unknown()).optional(),
});
export type ApiErrorBody = z.infer<typeof ApiErrorBody>;

export const ErrorEnvelope = z.object({
  error: ApiErrorBody,
  request_id: z.string(),
  schema_version: z.literal(SCHEMA_VERSION),
});

export function successEnvelope<T extends z.ZodType>(data: T) {
  return z.object({
    data,
    request_id: z.string(),
    schema_version: z.literal(SCHEMA_VERSION),
  });
}

export type Envelope<T> =
  | { data: T; request_id: string; schema_version: 1 }
  | { error: ApiErrorBody; request_id: string; schema_version: 1 };

export const Page = <T extends z.ZodType>(item: T) =>
  z.looseObject({ items: z.array(item), next_cursor: z.string().nullable().optional() });

/** Procedencia de un dato técnico: nunca se inventa. */
export const Provenance = z.enum(['unknown', 'declared', 'calculated', 'verified']);
export type Provenance = z.infer<typeof Provenance>;

export const MemberRole = z.enum(['viewer', 'editor', 'owner']);
export const ChannelLine = z.enum(['anime', 'gaming', 'organico', 'otro']);
export const VideoFormat = z.enum(['long', 'short']);
export const FormatScope = z.enum(['any', 'long', 'short']);
export const ProjectStage = z.enum(['brief', 'script', 'voice', 'direction', 'review', 'presentation', 'publication', 'done']);
export const DocumentKind = z.enum(['script', 'edit_plan', 'notes']);
export const RuleSeverity = z.enum(['blocking', 'warning', 'preference']);
export const Calibration = z.enum(['por_calibrar', 'calibrado', 'no_aplica']);
export const RuleFamily = z.enum([
  'composition',
  'avatar',
  'wink',
  'progression',
  'subtitles',
  'sound',
  'rhythm',
  'language',
  'structure',
  'marketing',
]);
export const AssetKind = z.enum(['video', 'audio_voice', 'music', 'sfx', 'ambience', 'image', 'avatar', 'subtitle', 'other']);
export const AssetOrigin = z.enum(['own', 'myinstants', 'mixkit', 'jamendo', 'generated', 'imported', 'other']);
export const Availability = z.enum(['available', 'missing', 'modified', 'permission_revoked', 'unknown']);
export const JobType = z.enum([
  'index',
  'probe',
  'transcribe',
  'align',
  'render_preview',
  'render_final',
  'export_capcut',
  'analyze_audio',
  'publish_transfer',
  'privacy_export',
]);
export const JobStatus = z.enum([
  'pending',
  'waiting_device',
  'claimed',
  'running',
  'verifying',
  'completed',
  'failed',
  'cancelled',
  'action_required',
]);
export const PublicationStatus = z.enum([
  'draft',
  'pending_authorization',
  'planned',
  'waiting_file',
  'transferring',
  'processing',
  'scheduled_confirmed',
  'published_confirmed',
  'failed',
  'action_required',
  'cancelled',
]);
export const Provider = z.enum(['youtube', 'tiktok', 'instagram', 'facebook', 'x', 'jamendo', 'ai_provider', 'other']);
export const Network = z.enum(['youtube', 'tiktok', 'instagram', 'facebook', 'x', 'other']);
export const RightsEvidenceStatus = z.enum([
  'own_declared',
  'license_documented',
  'permission',
  'public_domain_justified',
  'pending',
  'incompatible',
]);
export const IncidentKind = z.enum(['claim', 'block', 'third_party_monetization', 'takedown', 'strike']);
export const IncidentStatus = z.enum(['open', 'disputed', 'resolved']);
export const MarketingFamily = z.enum(['image', 'title', 'description', 'hashtags']);
export const ObjectPurpose = z.enum(['asset', 'rights_proof', 'marketing', 'metrics_import', 'publication_staging', 'export']);
